// src/shared/components/InstallmentCourseEntryGuard.jsx
import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function formatThaiDate(value) {
  const d = toDateSafe(value);
  if (!d) return "-";
  return d.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 0,
  }).format(n);
}

function getInstallmentList(enroll) {
  return Array.isArray(enroll?.installments) ? enroll.installments : [];
}

function getCurrentUnpaidInstallment(enroll) {
  if (enroll?.paymentType !== "installment") return null;
  return getInstallmentList(enroll).find((item) => item?.status !== "paid") || null;
}

function isPastDue(dueAt) {
  const due = toDateSafe(dueAt);
  if (!due) return false;
  return Date.now() > due.getTime();
}

function isWithinDaysBefore(dueAt, days = 3) {
  const due = toDateSafe(dueAt);
  if (!due) return false;

  const now = Date.now();
  const diff = due.getTime() - now;

  return diff >= 0 && diff <= days * 24 * 60 * 60 * 1000;
}

function isInstallmentEnrollOverdue(enroll) {
  const unpaid = getCurrentUnpaidInstallment(enroll);
  if (!unpaid?.dueAt) return false;
  return isPastDue(unpaid.dueAt);
}

function getUsableEnrolls(enrolls = []) {
  return enrolls.filter((enroll) => {
    if (enroll?.paymentType !== "installment") return true;
    return !isInstallmentEnrollOverdue(enroll);
  });
}

function getOverdueInstallmentEnrolls(enrolls = []) {
  return enrolls.filter((enroll) => {
    if (enroll?.paymentType !== "installment") return false;
    return isInstallmentEnrollOverdue(enroll);
  });
}

function getNearestWarningInstallment(enrolls = [], warningDays = 3) {
  for (const enroll of enrolls) {
    if (enroll?.paymentType !== "installment") continue;

    const unpaid = getCurrentUnpaidInstallment(enroll);
    if (!unpaid?.dueAt) continue;

    if (isWithinDaysBefore(unpaid.dueAt, warningDays)) {
      return {
        ...unpaid,
        enrollId: enroll?.id || "",
        courseId: enroll?.courseId || "",
        coursePackage: enroll?.coursePackage || "",
        coursePackageLabel: enroll?.coursePackageLabel || "",
      };
    }
  }

  return null;
}

export default function InstallmentCourseEntryGuard({
  course,
  onAllowed,
  warningDays = 3,
  children,
}) {
  const navigate = useNavigate();

  const [modalState, setModalState] = useState({
    open: false,
    mode: "info", // info | overdue
    title: "",
    message: "",
    payUrl: "",
  });

  const enrolls = useMemo(() => {
    return Array.isArray(course?.enrolls) ? course.enrolls : [];
  }, [course]);

  const usableEnrolls = useMemo(() => {
    return getUsableEnrolls(enrolls);
  }, [enrolls]);

  const overdueInstallmentEnrolls = useMemo(() => {
    return getOverdueInstallmentEnrolls(enrolls);
  }, [enrolls]);

  const warningInstallment = useMemo(() => {
    return getNearestWarningInstallment(enrolls, warningDays);
  }, [enrolls, warningDays]);

  const blockedInstallment = useMemo(() => {
    if (usableEnrolls.length > 0) return null;
    return overdueInstallmentEnrolls[0] || null;
  }, [usableEnrolls, overdueInstallmentEnrolls]);

  const payUrl = useMemo(() => {
    const targetEnroll = blockedInstallment || overdueInstallmentEnrolls[0] || null;
    if (!course?.id || !targetEnroll?.id) return "";
    return `/courses/${course.id}/payment-promptpay/${targetEnroll.id}`;
  }, [course, blockedInstallment, overdueInstallmentEnrolls]);

  const handleEnter = () => {
    // ไม่มี overdue และไม่มี warning → เข้าได้เลย
    if (!blockedInstallment && !warningInstallment) {
      if (typeof onAllowed === "function") onAllowed();
      return;
    }

    // ยังมี usable enroll อยู่ → ไม่ block ทั้งคอร์ส
    // ถ้าใกล้ครบกำหนดค่อยเตือน แต่ยังเข้าเรียนได้
    if (usableEnrolls.length > 0) {
      if (warningInstallment) {
        const dueText = formatThaiDate(warningInstallment?.dueAt);
        const amountText = formatBaht(warningInstallment?.amount || 0);
        const warningPayUrl =
          course?.id && warningInstallment?.enrollId
            ? `/courses/${course.id}/payment-promptpay/${warningInstallment.enrollId}`
            : "";

        setModalState({
          open: true,
          mode: "info",
          title: `ใกล้ถึงวันครบกำหนดชำระงวดที่ ${warningInstallment.installmentNo}`,
          message: `กรุณาชำระเงินงวดที่ ${warningInstallment.installmentNo} จำนวน ${amountText} ภายในวันที่ ${dueText} เพื่อสามารถใช้บริการได้ต่อเนื่อง`,
          payUrl: warningPayUrl,
        });
        return;
      }

      if (typeof onAllowed === "function") onAllowed();
      return;
    }

    // ไม่มี usable enroll เหลือ และมี overdue → block และพาไป payment
    if (blockedInstallment) {
      const unpaid = getCurrentUnpaidInstallment(blockedInstallment);
      const amountText = formatBaht(unpaid?.amount || 0);

      setModalState({
        open: true,
        mode: "overdue",
        title: `ครบกำหนดชำระงวดที่ ${unpaid?.installmentNo || "-" } แล้ว`,
        message: `กรุณาชำระเงินงวดที่ ${unpaid?.installmentNo || "-"} จำนวน ${amountText} เพื่อสามารถใช้บริการได้ต่อเนื่อง ระบบจะพาคุณไปหน้าชำระเงินของคอร์สนี้`,
        payUrl,
      });
      return;
    }

    if (typeof onAllowed === "function") onAllowed();
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  const handleConfirm = () => {
    const isOverdue = modalState.mode === "overdue";
    closeModal();

    if (isOverdue && modalState.payUrl) {
      navigate(modalState.payUrl);
      return;
    }

    if (typeof onAllowed === "function") onAllowed();
  };

  return (
    <>
      {typeof children === "function"
        ? children({
            handleEnter,
            warningInstallment,
            blockedInstallment,
            payUrl,
            usableEnrolls,
            overdueInstallmentEnrolls,
          })
        : children}

      <Modal show={modalState.open} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalState.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div>{modalState.message}</div>

          {modalState.payUrl ? (
            <div className="mt-3">
              <Link to={modalState.payUrl}>ไปหน้าชำระเงินงวดนี้</Link>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal}>
            ปิด
          </Button>

          <Button
            variant={modalState.mode === "overdue" ? "warning" : "primary"}
            onClick={handleConfirm}
          >
            ตกลง
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}